'use client';

import React, {
  Context,
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback, useContext,
  useEffect,
  useMemo, useRef,
  useState
} from 'react';
import { useMedia } from 'react-use';
import { useForm } from 'react-hook-form';
import NextImage from 'next/image';
import { useRouter } from 'next/navigation';
import { LoaderCircle } from 'lucide-react';
import dynamic from 'next/dynamic';

import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogPortal } from 'components/ui/dialog';
import { Label } from 'components/ui/label';
import { CheckIcon, KeyConnectIcon } from 'components/icons/v2';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { Sheet, SheetContent, SheetPortal } from 'components/ui/sheet';

import { useAuth } from 'contexts/auth';
import { RequestNode, UserNode } from 'generated/types';
import {
  useConnectConfirmMutation,
  useMessageMutation, useSaveOnBoardingFinishedMutation,
  useSendIntroMessageMutation,
  useTelegramConfigurationQuery,
  useWorkedWithConfirmMutation,
  useWorkedWithMutation
} from 'generated/hooks';

import { useToast } from 'hooks/useToast';
import { Avatar } from '../../_components/Avatar';
import { MEMBER_ROUTES } from 'utils/routes';
import { TelegramLogin } from 'components/TelegramLogin';
import { WhatsappLogin } from 'components/WhatsappLogin';
import successTelegramConnectAnimationData from '../../_components/animation/successTelegramConnectOneCommunity.json';
import { useMemberTracking } from 'app/_tracking/events';
import LinkedinContext from '../../../_context/LinkedinContext';
import { useTelegramMiniApp } from 'app/_context/TelegramContext';
import { Slider, ISliderRef } from 'app/(unauthenticated)/landing-v2/_components/slider';
import { CommunityInfoNode, CurrentUserType } from 'types';

const Lottie = dynamic(() => import('lottie-react'), { ssr: false });

interface IMemberContextProvider {
  children: ReactNode;
  communityInfo?: CommunityInfoNode | null;
  searchRequests: RequestNode[];
  user?: CurrentUserType | null;
}

type ActionType = 'connect' | 'help' | 'worked';

export type UserConnectType = Pick<NonNullable<UserNode>, 'id' | 'firstName' | 'lastName' | 'image' | 'connection'>

interface IMemberContext {
  connectModalUser: UserConnectType | null;
  suggestions: RequestNode[];
  setConnectModalUser: (user: UserConnectType) => void;
  setAction: Dispatch<SetStateAction<ActionType>>;
  setShowSuccessTelegramModal: Dispatch<SetStateAction<boolean>>;
  handleMessage: (id: string) => void;
  handleConnectConfirm: (confirm: boolean, id: string | null | undefined) => Promise<boolean>;
  handleWorkedWithConfirm: (confirm: boolean, id: string | null | undefined) => Promise<boolean>;
  handleGoToUserProfile: (userId: string) => void;
  mustHaveProfile: (callback: () => void) => void;
}

const defaultValues: IMemberContext = {
  connectModalUser: null,
  suggestions: [],
  setConnectModalUser: () => void 0,
  setAction: () => void 0,
  setShowSuccessTelegramModal: () => void 0,
  handleMessage: () => void 0,
  handleConnectConfirm: async () => false,
  handleWorkedWithConfirm: async () => false,
  handleGoToUserProfile: () => void 0,
  mustHaveProfile: () => void 0
};

export const MemberContext: Context<IMemberContext> = createContext(defaultValues);

const options = [
  {
    value: 'connect',
    label: 'Go to LinkedIn',
    description: (name: string) => `Redirect to LinkedIn for a connection request & DMs.`,
    icon: <KeyConnectIcon height={16} width={16} />
  },
  // {
  //   value: 'worked',
  //   label: "Let's Collaborate",
  //   description: (name: string) => `Send an ice breaker: this member will receive your collaboration request upon signing in`,
  //   icon: <ReloadIcon height={16} width={16} />
  // }
];

const SLIDER_IMAGES = ['/members/twa-onboarding/slide_1.png', '/members/twa-onboarding/slide_2.png', '/members/twa-onboarding/slide_3.png'];

export const MemberProvider: FC<IMemberContextProvider> = (props) => {
  const {
    children,
    communityInfo,
    searchRequests,
    user
  } = props;

  const {
    trackConnect,
    trackConnectModal,
    trackWorkedWithReaction,
    trackConnectReaction,
    trackMessage
  } = useMemberTracking();

  const router = useRouter();

  const isMobile = useMedia('(max-width: 768px)', false);

  const [telegramConfig] = useTelegramConfigurationQuery();
  const userConnectedApp = useMemo(() => !!user?.isTelegramConnected || !!user?.isWhatsappConnected, [user]);
  const appConnectionRequired = useMemo(() => {
    if (!communityInfo) return true;
    return communityInfo.membersConfirmation;
  }, [communityInfo]);

  const { handleToast } = useToast();
  const { isEnabledTWA, setMainButtonTWA, hideMainButtonTWA, offClickMainButtonTWA } = useTelegramMiniApp();
  const [connectModalUser, setConnectModalUser] = useState<UserConnectType | null>(null);
  const [action, setAction] = useState<ActionType>('connect');
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [showSuccessTelegramConnectModal, setShowSuccessTelegramModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');


  const { currentUser, reFetchCurrentUser } = useAuth();

  const isOnboardingFinished = useMemo(() =>
    user?.onboardingFinished || currentUser?.onboardingFinished || false,
    [currentUser?.onboardingFinished, user?.onboardingFinished]
  );

  const [connectState, setIntroMessage] = useSendIntroMessageMutation();
  const [workedWithState, setWorkedWith] = useWorkedWithMutation();
  const [onboardingFinishedState, saveOnboardingFinishedState] = useSaveOnBoardingFinishedMutation();

  const [messageState, messageMutation] = useMessageMutation();
  const [, connectConfirmMutation] = useConnectConfirmMutation();
  const [, workedWithConfirmMutation] = useWorkedWithConfirmMutation();

  const {
    control,
    handleSubmit,
    reset,
  } = useForm<{ message: string }>({
    mode: 'onChange',
    defaultValues: {
      message: '',
    }
  });

  const [currentSlide, setSlide] = useState<number>(0);
  const onboardingSliderRef = useRef<ISliderRef>(null);

  const { setLinkedinModalState } = useContext(LinkedinContext);

  useEffect(() => {
    if (!connectModalUser) {
      reset();
    }
  }, [connectModalUser, reset]);

  const handleRedirect = useCallback((link: string) => {
    trackMessage('redirected');
    window.open(link, '_target');
    router.refresh();
  }, [router, trackMessage]);

  const handleConnect = useCallback(async (data?: { message: string }) => {
    try {
      setErrorMessage(() => '');
      const response = await setIntroMessage({
        toUserId: connectModalUser!.id,
        message: data?.message ?? '',
      });

      if (response.error) {
        console.log(response.error?.message);
        setErrorMessage(response.error.message);
        return;
      }

      setConnectModalUser(() => null);

      if (response.data?.connect?.connectLink) {
        handleRedirect(response.data?.connect?.connectLink ?? '');
      } else {
        router.refresh();
      }

    } catch (error) {
      console.log(error);
    }
  }, [connectModalUser, handleRedirect, router, setIntroMessage]);

  const handleWorkedWith = useCallback(async () => {
    try {
      setErrorMessage(() => '');
      const response = await setWorkedWith({
        userId: connectModalUser!.id
      });

      if (response.error) {
        console.log(response.error.message);
        setErrorMessage(response.error.message);
        return;
      }

      setConnectModalUser(() => null);
      setShowSuccessModal(() => true);
    } catch (error) {
      console.log(error);
    }
  }, [connectModalUser, setWorkedWith]);

  const handleSendRequest = useCallback(async (data?: { message: string }) => {
    trackConnect('sent');
    if (action === 'connect') {
      await handleConnect(data);
    } else if (action === 'worked') {
      await handleWorkedWith();
    }
  }, [action, handleConnect, handleWorkedWith, trackConnect]);


  const handleMessage = useCallback(async (id: string) => {
    if (!currentUser?.profileStatus) {
      setLinkedinModalState(() => 'WELCOME');
      return;
    }

    try {
      trackMessage('clicked');

      const response = await messageMutation({
        toUserId: id
      });

      if (response.error) {
        handleToast(response.error.message, 'error');
        console.error(response.error);
        return;
      }

      handleRedirect(response.data?.message?.connectLink ?? '');
    } catch (error) {
      console.log(error);
    }
  }, [currentUser?.profileStatus, handleRedirect, handleToast, messageMutation, setLinkedinModalState, trackMessage]);

  const handleConnectConfirm = useCallback(async (confirm: boolean, connectId: string | null | undefined): Promise<boolean> => {
    if (!currentUser?.profileStatus) {
      setLinkedinModalState(() => 'WELCOME');
      return false;
    }

    try {
      trackConnectReaction();

      const response = await connectConfirmMutation({
        confirm,
        connectId: connectId as string
      });

      if (response.error) {
        handleToast(response.error.message, 'error');
        console.log(response.error);
        return false;
      }

      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }, [connectConfirmMutation, currentUser?.profileStatus, handleToast, setLinkedinModalState, trackConnectReaction]);

  const handleWorkedWithConfirm = useCallback(async (confirm: boolean, workedWithId: string | null | undefined): Promise<boolean> => {
    if (!currentUser?.profileStatus) {
      setLinkedinModalState(() => 'WELCOME');
      return false;
    }

    try {
      trackWorkedWithReaction();
      const response = await workedWithConfirmMutation({
        confirm,
        workedWithId: workedWithId as string
      });

      if (response.error) {
        handleToast(response.error.message, 'error');
        console.log(response.error);
        return false;
      }

      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }, [currentUser?.profileStatus, handleToast, setLinkedinModalState, trackWorkedWithReaction, workedWithConfirmMutation]);

  const handleSetConnectModalUser = useCallback((user: UserConnectType) => {
    if (!currentUser?.profileStatus) {
      setLinkedinModalState(() => 'WELCOME');
      return;
    }
    trackConnectModal('opened');
    setConnectModalUser(() => user);
  }, [currentUser?.profileStatus, setLinkedinModalState, trackConnectModal]);

  const handleGoToUserProfile = useCallback((id: string) => {
    if (!currentUser?.profileStatus) {
      setLinkedinModalState(() => 'WELCOME');
      return;
    }

    router.push(MEMBER_ROUTES.MEMBER_PROFILE.getPath(id));
  }, [currentUser?.profileStatus, router, setLinkedinModalState]);

  const mustHaveProfile = useCallback((onHasProfile: () => void) => {
    if (!currentUser?.profileStatus) {
      setLinkedinModalState(() => 'WELCOME');
      return;
    }
    onHasProfile();
  }, [currentUser?.profileStatus, setLinkedinModalState]);

  const value: IMemberContext = useMemo(() => ({
    suggestions: searchRequests,
    connectModalUser,
    setConnectModalUser: handleSetConnectModalUser,
    setAction,
    setShowSuccessTelegramModal,
    handleMessage,
    handleConnectConfirm,
    handleWorkedWithConfirm,
    handleGoToUserProfile,
    mustHaveProfile,
  }), [connectModalUser, handleConnectConfirm, handleGoToUserProfile, handleMessage, handleSetConnectModalUser,
    handleWorkedWithConfirm, searchRequests, mustHaveProfile
  ]);

  const telegramLogin = useMemo(() => {
    return <>
      <TelegramLogin
        botHandle={telegramConfig.data?.telegramConfiguration?.botHandle}
        onSuccess={(communitiesFound: number) => {
          reFetchCurrentUser();
          router.prefetch(MEMBER_ROUTES.MEMBERS.getPath());
          if (communitiesFound) {
            setShowSuccessTelegramModal(() => true);
          }
        }}
      />
    </>
  }, [reFetchCurrentUser, router, telegramConfig.data?.telegramConfiguration?.botHandle]);

  const DialogRoot = isMobile ? Dialog : Sheet;
  const Portal = isMobile ? DialogPortal : SheetPortal;
  const Content = isMobile ? DialogContent : SheetContent;

  /* Telegram Mini App */
  useEffect(() => {
    if (!connectModalUser) return;

    setMainButtonTWA('Send Request', () => {
      handleSendRequest();
      hideMainButtonTWA(handleSendRequest);
    });

    return () => {
      hideMainButtonTWA(handleSendRequest);
    };
  }, [connectModalUser, setMainButtonTWA, hideMainButtonTWA, handleSendRequest]);

  const showOnboarding = useMemo(() => {
    return isEnabledTWA && !isOnboardingFinished;
  }, [isEnabledTWA, isOnboardingFinished]);

  useEffect(() => {
    if (!showOnboarding) return;
    const handler = async () => {
      if (currentSlide === SLIDER_IMAGES.length - 1) {
        try {
          const response = await saveOnboardingFinishedState({});

          if (response.data?.saveOnboardingFinished?.ok) {
            reFetchCurrentUser();
            hideMainButtonTWA(handler);
          }
        } catch (error) {
          console.error(error);
        }
      } else {
        onboardingSliderRef.current?.changeSlide(currentSlide + 1);
      }
    };

    setMainButtonTWA(currentSlide == SLIDER_IMAGES.length - 1 ? "Let's go" : 'Next', handler);

    return () => {
      offClickMainButtonTWA(handler);
    };
  }, [currentSlide, offClickMainButtonTWA, hideMainButtonTWA, saveOnboardingFinishedState, setMainButtonTWA, showOnboarding, reFetchCurrentUser]);

  return (
    <MemberContext.Provider value={value}>
      {showOnboarding ? (
        <div className="h-[100svh] bg-white fixed top-0 left-0 z-50 w-full flex flex-col">
          <Slider
            ref={onboardingSliderRef}
            autoPlay={false}
            autoPlayInterval={3000}
            perPage={1}
            dynamicPerPage={false}
            config={{
              loop: false,
              slider: {
                wrapperClassNames: 'overflow-hidden h-full',
              },
              slide: {
                wrapperClassNames: 'h-full',
              },
              pagination: {
                dotsContainerStyle: 'bottom-6',
                activeDotStyle: 'bg-brand-500',
                inactiveDotStyle: 'bg-gray-50',
              }
            }}
            sliderIndexListener={(slide) => setSlide(() => slide)}
          >
            {SLIDER_IMAGES.map((image, index) => (
              <div key={index} className="h-full relative w-full">
                <NextImage
                  priority
                  width={375}
                  height={746}
                  src={image}
                  alt={`Welcome Slider Image ${index + 1}`}
                  className="absolute right-0 top-1/2 -translate-y-1/2 h-full w-full object-cover object-top"
                />
              </div>
            ))}
          </Slider>
        </div>
      ) : (
        <>
          {children}
          {showSuccessTelegramConnectModal && (
            <div className="fixed z-50 top-0 left-0 w-full h-screen bg-black/30">
              <Lottie
                className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] md:w-[360px]"
                animationData={successTelegramConnectAnimationData}
                loop={false}
                onComplete={() => {
                  setShowSuccessTelegramModal(() => false);
                  router.refresh();
                }}
              />
            </div>
          )}
          <Dialog open={!!connectModalUser} onOpenChange={() => {
            trackConnectModal('closed');
            setConnectModalUser(null);
          }}>
            <DialogContent className='p-0 sm-1/2:max-w-full sm-1/2:w-full sm-1/2:h-full sm-1/2:!rounded-none md:!rounded-radius-4xl'>
              <div className="px-6 pb-6 pt-8 bg-gray-50 gap-[18px] flex flex-col items-center justify-center">
                <div className="rounded-full overflow-hidden border-4 border-white shadow-lg">
                  <Avatar
                    avatar={connectModalUser?.image}
                    user={connectModalUser}
                    alt={connectModalUser?.firstName}
                    width={160}
                    height={160}
                    defaultIconHeight={80}
                    defaultIconWidth={80}
                    containerClassname="text-display-xl text-gray-500 font-semibold"
                  />
                </div>
                <div>
                  <p
                    className="text-display-sm text-black font-bold">{connectModalUser?.firstName} {connectModalUser?.lastName}</p>
                </div>
                <div className="flex flex-col gap-5 w-full">
                  <div className="flex flex-col gap-4">
                    <RadioGroup
                      value={action}
                      onValueChange={(value: ActionType) => {
                        setErrorMessage(() => '');
                        setAction(() => value);
                        trackConnect(value === 'worked' ? 'worked_with_selected' : 'connect_selected');
                      }}
                    >
                      {options.map((item, index) => (
                        <Label htmlFor={item.value} key={index}>
                          <div
                            className={`p-4 flex gap-1 items-start rounded-radius-xl ${item.value === action ? 'border-2 border-brand-500 bg-brand-50' : 'border border-gray-100'}`}>
                            <div
                              className={`w-8 h-8 rounded-radius-sm border border-gray-100 flex items-center justify-center ${item.value === action ? 'text-brand-600' : 'text-gray-600'}`}>
                              {item.icon}
                            </div>
                            <div className="flex flex-col gap-[2px] flex-1">
                              <p className="text-small text-gray-700 font-medium">{item.label}</p>
                              <p
                                className="text-small text-gray-600">{item.description(connectModalUser?.firstName ?? '')}</p>
                            </div>
                            <RadioGroupItem value={item.value} id={item.value} checked={item.value === action} />
                          </div>
                        </Label>
                      ))}
                    </RadioGroup>
                  </div>
                </div>
                {!!errorMessage && (
                  <div className="w-full">
                    <span className="text-sm text-red text-left">{errorMessage}</span>
                  </div>
                )}
                {!isEnabledTWA && (
                  <Button
                    disabled={connectState.fetching || workedWithState.fetching}
                    size='medium'
                    className='w-full'
                    variant='primaryBrand'
                    onClick={handleSubmit(handleSendRequest)}
                  >
                    {connectState.fetching || workedWithState.fetching && (
                      <LoaderCircle className='animate-spin' />
                    )}
                    Send Request
                  </Button>
                )}
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={showSuccessModal} onOpenChange={() => {
            setShowSuccessModal(false);
            router.refresh();
          }}>
            <DialogContent className="p-0 max-w-[375px]">
              <div className="pt-8 px-6 pb-6 flex flex-col gap-[18px] items-center">
                <div
                  className="w-24 h-24 rounded-full overflow-hidden border-2 border-white bg-brand-100 shadow-xl flex justify-center items-center text-brand-600">
                  <CheckIcon width={55} height={55} />
                </div>
                <div className="text-center">
                  <p className="text-display-sm font-bold text-black mb-3">Great start! Find more collaborators</p>
                </div>
                <Button
                  variant="primaryBrand"
                  size="medium"
                  className="w-full"
                  onClick={() => {
                    setShowSuccessModal(false);
                    router.refresh();
                  }}
                >
                  Continue
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {(false && !isEnabledTWA && appConnectionRequired && !userConnectedApp) && (
            <DialogRoot open>
              {/* HIDDEN as not used on production. Will be deleted once 100% sure to delete */ }
              <Portal>
                <Content
                  hideCloseIcon
                  className="bg-white border-0 outline-none text-black p-[70px] max-w-[700px] items-center flex rounded-l-4xl md:!rounded-4xl md:!px-6 md:!py-8 md:!max-w-[96%] md:!w-full">
                  <div className="flex flex-col items-center text-center flex-1">
                    <>
                      <Avatar
                        width={160}
                        height={160}
                        avatar={user?.image}
                        user={user as UserNode}
                        alt={`${user?.firstName} ${user?.lastName}`}
                        containerClassname="shadow-lg border-4 border-white mb-[30px] md:mb-6 text-display-xl text-gray-500 font-semibold"
                        defaultIconHeight={80}
                        defaultIconWidth={80}
                      />
                      <p className="text-display-lg text-brand-950 font-semibold mb-3 md:text-display-md md:mb-1">
                        {user?.firstName ? `${user?.firstName},` : ''} <br /> Connect to Your <span
                        className="font-normal">Community</span>
                      </p>
                      <p className="text-gray-800 text-large mb-10 md:text-medium md:mb-6">
                        Confirm your membership on {
                        communityInfo?.app === 'TELEGRAM' ? 'Telegram' : (
                          communityInfo?.app === 'WHATSAPP') ? 'Whatsapp' :
                          'Telegram or Whatsapp'
                        } to connect with others and uncover exciting opportunities in your network
                      </p>
                      <div className='flex w-full gap-4 flex-col'>
                        {communityInfo?.app === 'TELEGRAM' ? (
                          // TODO: fucking get rid of telegramLogin
                          telegramLogin
                        ) : (communityInfo?.app === 'WHATSAPP') ? (
                          <WhatsappLogin />
                        ) : <>
                          {telegramLogin}
                          or
                          <WhatsappLogin />
                        </>}
                      </div>
                    </>
                  </div>
                </Content>
              </Portal>
            </DialogRoot>
          )}
        </>
      )}
    </MemberContext.Provider>
  );
};

export default MemberProvider;
